/**
 * A standing series on screen (migration 0069).
 *
 * Which weekdays, every how many weeks, until when. The database lays the same
 * series out again in app.series_instants; this is what lets the booking dialog
 * say "12 turnos, hasta el 30 de noviembre" before anything is booked.
 *
 * Weekdays are ISO numbers, 1 Monday to 7 Sunday, to match extract(isodow).
 * Days travel as YYYY-MM-DD keys on the practice's calendar, as everywhere else
 * (lib/practiceTime.js).
 */

import { addDaysToKey, weekStartKey } from './practiceTime';

/** Monday first, the way a week is read here. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/** The most sessions one series can book, as the database also refuses. */
export const MAX_SERIES = 200;

/** The ISO weekday of a day key: 1 Monday … 7 Sunday. */
export const isoWeekday = (key) => {
  const [year, month, day] = String(key).split('-').map(Number);
  return ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1;
};

/**
 * Every day the series lands on, as day keys.
 *
 * It starts at the session being booked: earlier days of that first week are
 * not part of it, even when their weekday is chosen. `untilKey` is included.
 * More than MAX_SERIES days is still counted, so the dialog can say the series
 * is too long instead of silently booking a shorter one.
 */
export const seriesDays = (startKey, weekdays, intervalWeeks = 1, untilKey = null) => {
  if (!startKey || !untilKey || untilKey < startKey) return [];

  const chosen = [...new Set((weekdays ?? []).map(Number))]
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7)
    .sort((a, b) => a - b);
  if (chosen.length === 0) return [];

  const step = Math.min(8, Math.max(1, Math.trunc(Number(intervalWeeks)) || 1));
  const days = [];
  let monday = weekStartKey(startKey);
  while (monday <= untilKey && days.length <= MAX_SERIES) {
    for (const weekday of chosen) {
      const day = addDaysToKey(monday, weekday - 1);
      if (day >= startKey && day <= untilKey) days.push(day);
    }
    monday = addDaysToKey(monday, step * 7);
  }
  return days;
};
